// =============================================================================
// ui/tabs/GoalsEditor.tsx — Settings → Preferences → Goals.
// -----------------------------------------------------------------------------
// ── 🚨 THE THIRD SETTING THAT WAS READ AND NOT WRITABLE ──
//
// The Dashboard has drawn Goals Progress since it shipped — names, periods,
// account chips, progress bars — off rows nothing in this client could create,
// edit or delete. Same shape as Default Risk and the sidebar before it: the
// EFFECT is on screen and the CAUSE is not, which is worse than a missing
// feature because there is nothing to notice.
//
// ── 🚨 ALL FOUR TEMPLATES ARE OFFERED, AND THREE SAY THEY ARE NOT MEASURED ──
//
// This client computes the dollar target only. It would be tidier to offer just
// that one, and it would corrupt data: a stored `tag-cap` goal opened on a
// `<select>` with no matching option lands on the first entry, and Save rewrites
// it as a dollar target. So every template is selectable, and the ones the
// Goals card cannot compute say so ON THE FORM — the same words the card uses,
// so the two screens cannot disagree about it.
//
// ── ⚠ THE PARAMS BOX IS ONE BLOB ACROSS TEMPLATES, LIKE BETA'S ──
//
// Flipping template mid-edit does not wipe what you typed for the previous one;
// only Save narrows the blob to the active template's keys. See
// `domain/goalForm.ts` note 3.
// =============================================================================

import { useState } from 'react';
import { useAccounts } from '../state/accounts';
import { usePreferences } from '../state/preferences';
import { useSupabase } from '../state/supabase';
import { useGoalCatalogue } from '../state/useGoalCatalogue';
import { newTradeId } from '../../io/sync/assembleImported';
import { deleteGoal, insertGoal, updateGoal } from '../../io/sync/saveGoal';
import { goalAccountLabel, type Goal } from '../../selectors/goals';
import {
  ADHERENCE_BOOLEANS, ADHERENCE_SCOPES, GOAL_PERIOD_OPTIONS, GOAL_TEMPLATES,
  STREAK_BOOLEANS, draftFromGoal, newGoalDraft, orderAccountsForPicker,
  paramChoice, paramNumber, templateIsMeasured, toggleAccountId,
  type GoalDraft,
} from '../../selectors/goalForm';

/** Which goal the modal is on: a loaded row, or `'new'`. */
type Editing = { goal: Goal | null } | null;

export function GoalsEditor() {
  const { phase, goals, error, reload } = useGoalCatalogue();
  const [editing, setEditing] = useState<Editing>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const { sb, userId } = useSupabase();
  const { accounts } = useAccounts();

  const nameOf = (id: string): string | null =>
    (accounts ?? []).find((a) => String(a.id) === id)?.name ?? null;

  const remove = async (id: string) => {
    setBusyId(id);
    setRowError(null);
    const { error: e } = await deleteGoal(sb, userId, id);
    setBusyId(null);
    setConfirmId(null);
    if (e) { setRowError(e); return; }
    reload();
  };

  return (
    // ⚠ A `data-testid`, NOT a class. Tests need a handle to scope to — the
    // Preferences pane has three other lists on it — and a class that exists
    // only for that is a rule waiting to be written by someone who assumes it
    // already does something.
    <section className="card" data-testid="settings-goals">
      {/* ⚠ The COUNT is in the heading, as in the old client. It is the one
          number that says at a glance whether a goal you expected to see is
          missing versus merely scrolled past. */}
      <h3>Goals{goals ? ` (${goals.length})` : ''}</h3>
      <p className="muted">
        Period targets, drawn on the Dashboard. A goal measures its own window —
        a daily target still reads today even when the rest of the app is
        filtered to last month.
      </p>

      {rowError ? <p className="red" role="alert">{rowError}</p> : null}

      {phase === 'failed' ? (
        // ⚠ NOT "No goals yet". One is a read that failed and the other is an
        // instruction to go and set some — and the second invites the user to
        // re-create targets that already exist.
        <p className="muted" role="alert">
          Couldn’t load your goals. {error}{' '}
          <button type="button" className="btn" onClick={reload}>Try again</button>
        </p>
      ) : !goals ? (
        <p className="muted">Loading your goals…</p>
      ) : goals.length === 0 ? (
        <p className="muted">No goals yet.</p>
      ) : (
        <div className="goal-edit-list">
          {goals.map((g) => (
            <div className="goal-edit-row" key={g.id}>
              <span className="grow">
                {g.name}{' '}
                <span className="muted">({g.template} · {g.period})</span>{' '}
                <span className="tag">{goalAccountLabel(g, nameOf)}</span>
              </span>

              {confirmId === g.id ? (
                <>
                  <span className="muted goal-edit-confirm">Delete this goal?</span>
                  <button type="button" className="btn" disabled={busyId === g.id}
                    onClick={() => setConfirmId(null)}>Cancel</button>
                  <button type="button" className="btn danger" disabled={busyId === g.id}
                    onClick={() => void remove(g.id)}>
                    {busyId === g.id ? 'Deleting…' : 'Delete'}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="btn"
                    onClick={() => setEditing({ goal: g })}>Edit</button>
                  {/* ⚠ ASKS FIRST. Beta puts a confirm dialog on the same
                      button, and a goal is a target someone set months ago —
                      there is no undo and nothing else records it. */}
                  <button type="button" className="btn danger"
                    aria-label={`Delete ${g.name}`}
                    onClick={() => { setRowError(null); setConfirmId(g.id); }}>×</button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="row gap-8 goal-edit-actions">
        <button
          type="button" className="btn primary"
          // ⚠ Disabled until the read lands. Adding a goal against an unknown
          // list is how you end up with two "Daily $500" rows.
          disabled={!goals}
          onClick={() => setEditing({ goal: null })}
        >+ New Goal</button>
      </div>

      {editing ? (
        <GoalForm
          goal={editing.goal}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The form
// ---------------------------------------------------------------------------

function GoalForm(
  { goal, onCancel, onSaved }:
  { goal: Goal | null; onCancel: () => void; onSaved: () => void },
) {
  const { sb, userId } = useSupabase();
  const { accounts } = useAccounts();
  const { prefs } = usePreferences();

  // ⚠ Seeded ONCE from a prop that is already resolved — the goal was loaded
  // before the modal opened. This is not the D86 shape: nothing here arrives
  // later, so there is nothing to catch up to.
  const [draft, setDraft] = useState<GoalDraft>(
    () => (goal ? draftFromGoal(goal) : newGoalDraft()),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tags = (prefs?.customMistakeTags ?? []) as readonly string[];
  const booleanOptions = draft.template === 'streak' ? STREAK_BOOLEANS : ADHERENCE_BOOLEANS;
  const booleanKeys = booleanOptions.map((b) => b.key);

  const set = <K extends keyof GoalDraft>(key: K, value: GoalDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const setParam = (key: string, value: unknown) =>
    setDraft((d) => ({ ...d, params: { ...d.params, [key]: value } }));

  const save = async () => {
    setBusy(true);
    setError(null);
    // ⚠ The option lists go WITH the save, so a select resolves against what was
    // on screen. A goal pointing at a flag that no longer exists would otherwise
    // be written back with the dead key the user could not see.
    const choices = { booleanKeys, tags };
    const res = goal
      ? await updateGoal(sb, goal.id, draft, goal, userId, choices)
      : await insertGoal(sb, newTradeId(), draft, userId, choices);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    onSaved();
  };

  const measured = templateIsMeasured(draft.template);

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true"
      aria-label={goal ? 'Edit Goal' : 'New Goal'}>
      <div className="modal">
        <header className="modal-head">
          <b>{goal ? 'Edit Goal' : 'New Goal'}</b>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>×</button>
        </header>

        <div className="modal-body">
          <div className="form-grid cols-2">
            <label className="field full">
              <span className="field-label">Name</span>
              <input
                type="text" value={draft.name} placeholder="Daily $ target" disabled={busy}
                onChange={(e) => set('name', e.target.value)}
              />
              {/* ⚠ States beta's fallback rather than blocking the save. */}
              <span className="muted field-hint">Left blank, it saves as “Untitled goal”.</span>
            </label>

            <label className="field">
              <span className="field-label">Template</span>
              <select value={draft.template} disabled={busy}
                onChange={(e) => set('template', e.target.value)}>
                {GOAL_TEMPLATES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field-label">Period</span>
              <select value={draft.period} disabled={busy}
                onChange={(e) => set('period', e.target.value)}>
                {GOAL_PERIOD_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </label>

            {measured ? null : (
              <div className="field full">
                {/* ⚠ "not tracked here yet" IS THE DASHBOARD CARD'S OWN WORDING.
                    Two screens stating one fact must state it identically, or
                    they read as two different facts. */}
                <p className="muted goal-edit-unmeasured" role="note">
                  This template is <b>not tracked here yet</b>. The goal saves
                  normally and the current client still measures it — the
                  Dashboard card here will say so rather than draw a bar.
                </p>
              </div>
            )}

            <div className="field full">
              <span className="field-label">Applies to accounts</span>
              <AccountPicker
                selected={draft.accountIds}
                accounts={accounts}
                disabled={busy}
                onToggle={(id) => set('accountIds', toggleAccountId(draft.accountIds, id))}
              />
              <span className="muted field-hint">
                Leave all unticked for “all accounts”. A tagged goal shows on the
                Dashboard whenever any of its accounts is in the active filter.
              </span>
            </div>

            <div className="field full">
              <ParamFields
                template={draft.template}
                params={draft.params}
                booleanOptions={booleanOptions}
                tags={tags}
                disabled={busy}
                onChange={setParam}
              />
            </div>
          </div>

          {error ? <p className="red" role="alert">{error}</p> : null}
        </div>

        <footer className="modal-foot">
          <span className="grow" />
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function AccountPicker(
  { selected, accounts, disabled, onToggle }: {
    selected: readonly string[];
    accounts: readonly { id: string; name: string; archived?: boolean; excludeFromMetrics?: boolean }[] | null;
    disabled?: boolean;
    onToggle: (id: string) => void;
  },
) {
  // ⚠ NULL is not empty. A failed accounts read must not say "No accounts
  // defined" — that reads as a fact about the user's book.
  if (accounts === null) return <span className="muted">Account list unavailable.</span>;
  if (!accounts.length) return <span className="muted">No accounts defined.</span>;

  return (
    <div className="goal-acct-picker">
      {orderAccountsForPicker(accounts).map((a) => {
        const id = String(a.id);
        const on = selected.includes(id);
        return (
          <label key={id} className={`tag-pick${on ? ' on' : ''}`}>
            <input type="checkbox" checked={on} disabled={disabled}
              onChange={() => onToggle(id)} />
            {a.name || '(unnamed)'}
            {a.archived ? <span className="muted"> · archived</span> : null}
          </label>
        );
      })}
    </div>
  );
}

/**
 * The per-template inputs.
 *
 * ⚠ Every control here is CONTROLLED off the draft, so the value that gets
 * saved is the value on screen — including the selects, whose displayed choice
 * is resolved by `paramChoice` rather than left to the browser's "first option
 * wins". A goal pointing at a removed flag would otherwise show one thing and
 * save another.
 */
function ParamFields(
  { template, params, booleanOptions, tags, disabled, onChange }: {
    template: string;
    params: Record<string, unknown>;
    booleanOptions: readonly { key: string; label: string }[];
    tags: readonly string[];
    disabled?: boolean;
    onChange: (key: string, value: unknown) => void;
  },
) {
  const num = (key: string) => (
    <input
      type="number" step="any" disabled={disabled}
      value={paramNumber(params, key)}
      onChange={(e) => onChange(key, e.target.value === '' ? 0 : Number(e.target.value))}
    />
  );

  if (template === 'dollar-pl') {
    return (
      <label className="field">
        <span className="field-label">Target $</span>
        {num('amount')}
      </label>
    );
  }

  if (template === 'boolean-adherence') {
    const keys = booleanOptions.map((b) => b.key);
    return (
      <div className="form-grid cols-3">
        <label className="field">
          <span className="field-label">Boolean</span>
          <select value={paramChoice(params, 'booleanKey', keys)} disabled={disabled}
            onChange={(e) => onChange('booleanKey', e.target.value)}>
            {booleanOptions.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field-label">Scope</span>
          <select
            value={paramChoice(params, 'scope', ADHERENCE_SCOPES.map((s) => s.value))}
            disabled={disabled}
            onChange={(e) => onChange('scope', e.target.value)}
          >
            {ADHERENCE_SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field-label">Threshold %</span>
          {num('threshold')}
        </label>
      </div>
    );
  }

  if (template === 'tag-cap') {
    return (
      <div className="form-grid cols-2">
        <label className="field">
          <span className="field-label">Tag</span>
          {tags.length ? (
            <select value={paramChoice(params, 'tag', tags)} disabled={disabled}
              onChange={(e) => onChange('tag', e.target.value)}>
              {tags.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          ) : (
            // ⚠ SAYS WHY it is empty. An empty select would save an empty tag
            // and the goal would silently count nothing forever.
            <span className="muted">
              No mistake tags yet — add some under Mistake Tags above first.
            </span>
          )}
        </label>
        <label className="field">
          <span className="field-label">Max count</span>
          {num('max')}
        </label>
      </div>
    );
  }

  if (template === 'streak') {
    const keys = booleanOptions.map((b) => b.key);
    return (
      <div className="form-grid cols-2">
        <label className="field">
          <span className="field-label">Boolean</span>
          <select value={paramChoice(params, 'booleanKey', keys)} disabled={disabled}
            onChange={(e) => onChange('booleanKey', e.target.value)}>
            {booleanOptions.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field-label">Days</span>
          {num('days')}
        </label>
      </div>
    );
  }

  // ⚠ An unrecognised template — reachable only from a row written by something
  // other than this form. Rendering nothing at all would leave the user editing a
  // goal with invisible settings and no hint they exist (D61).
  //
  // ⚠ They are NOT at risk: an unknown template owns no keys, so the save spreads
  // the stored blob through untouched. Changing the template away is what drops
  // them, and that is true of every template here.
  return (
    <p className="muted" role="note">
      This client doesn’t know the “{template}” template, so its settings aren’t
      shown here. They’re kept as they are — unless you change the template
      above, which replaces them.
    </p>
  );
}
